'use client'

import { ReactNode, useMemo } from 'react'
import { RouterInfoProvider, useRouterInfo } from 'shared'
import type { CraftGlobals } from 'tsconfig/craft-types'
import { HeaderContextProvider } from 'ui/context/header'
import { MastheadContextProvider } from 'ui/context/masthead'
import { PinnedButtonContextProvider } from 'ui/context/pinned-button'
import DefaultLayout from '../../layouts/default'

type Props = {
  /** Template route, what the Pages Router had in router.route, e.g. "/procedure" */
  route: string
  /** Visible path, what the Pages Router had in router.asPath, e.g. "/verfahren" */
  asPath: string
  /** Renders header, footer and modals around the page (DefaultLayout) */
  global?: CraftGlobals
  transparentHeader?: boolean
  headerTheme?: string
  children: ReactNode
}

/**
 * Per-page client context that _app provided in the Pages Router: the header,
 * masthead and pinned-button contexts (seeded with the page's header props),
 * the page's router values, and DefaultLayout when the page has globals.
 */
export function SiteShell({ route, asPath, global, transparentHeader, headerTheme, children }: Props) {
  const site = useRouterInfo()
  const routerInfo = useMemo(() => ({ ...site, route, pathname: route, asPath }), [site, route, asPath])

  return (
    <RouterInfoProvider value={routerInfo}>
      <HeaderContextProvider initTransparent={transparentHeader ?? false} initTheme={headerTheme ?? 'light'}>
        <MastheadContextProvider>
          <PinnedButtonContextProvider>
            {global ? <DefaultLayout global={global}>{children}</DefaultLayout> : children}
          </PinnedButtonContextProvider>
        </MastheadContextProvider>
      </HeaderContextProvider>
    </RouterInfoProvider>
  )
}
